import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { UserPlus, Key, Trash2, Loader2, Search, ArrowLeft, User, Mail, Phone, MapPin, FileText, Download, GraduationCap, Briefcase, Wrench, Languages, Plus, Camera, Upload, Eye, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { downloadAuthFile } from "@/contexts/AuthContext";
import { usePagination, PaginationBar } from "@/components/PaginationBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement({ userRole }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPin, setShowPin] = useState(null);
  const [form, setForm] = useState({ codice_fiscale: "", nome: "", cognome: "", email: "", telefono: "", ruolo: "iscritto", sezione: "", indirizzo: "", data_nascita: "", nazionalita: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [scheda, setScheda] = useState(null);
  const [editingCV, setEditingCV] = useState(null);
  const [cvForm, setCvForm] = useState({});
  const [editAnag, setEditAnag] = useState(false);
  const [anagForm, setAnagForm] = useState({});
  const [photoUrl, setPhotoUrl] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [presetComp, setPresetComp] = useState([]);
  const [presetLingue, setPresetLingue] = useState([]);

  const fetchUsers = async () => {
    try { const { data } = await axios.get(`${API}/admin/users`); setUsers(data); }
    catch { toast.error("Errore caricamento utenti"); }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
    axios.get(`${API}/preset/competenze`).then(r => setPresetComp(r.data)).catch(() => {});
    axios.get(`${API}/preset/lingue`).then(r => setPresetLingue(r.data)).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.codice_fiscale || !form.nome || !form.cognome) { toast.error("Compila i campi obbligatori"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/users`, form);
      toast.success("Utente creato!");
      setShowCreate(false);
      setShowPin(data.pin);
      setForm({ codice_fiscale: "", nome: "", cognome: "", email: "", telefono: "", ruolo: "iscritto", sezione: "", indirizzo: "", data_nascita: "", nazionalita: "", pin: "" });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const handleDelete = async (id) => { if (!window.confirm("Eliminare?")) return; try { await axios.delete(`${API}/admin/users/${id}`); toast.success("Eliminato"); fetchUsers(); } catch { toast.error("Errore"); }};
  const handleResetTotp = async (id) => {
    if (!window.confirm("Sei sicuro di voler resettare PIN e Password?")) return;
    try { const { data } = await axios.post(`${API}/admin/users/${id}/reset-totp`, {}); setShowPin(data); toast.success("Nuove credenziali generate"); } catch { toast.error("Errore"); }
  };
  const handleShowTotp = async (id) => {
    try { const { data } = await axios.get(`${API}/admin/users/${id}/totp`); setShowPin(data); } catch { toast.error("Errore"); }
  };

  const openScheda = async (userId) => {
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/scheda`);
      setScheda(data);
      setPhotoUrl(null);
      if (data.foto_path) {
        try {
          const resp = await axios.get(`${API}/files/${data.foto_path}`, { responseType: 'blob', validateStatus: s => s === 200 });
          if (resp.status === 200) setPhotoUrl(URL.createObjectURL(resp.data));
        } catch (e) { console.error(e); }
      }
    } catch { toast.error("Errore caricamento scheda"); }
  };

  const openEditAnag = () => {
    setAnagForm({ nome: scheda.nome, cognome: scheda.cognome, email: scheda.email || "", telefono: scheda.telefono || "", indirizzo: scheda.indirizzo || "", data_nascita: scheda.data_nascita || "", nazionalita: scheda.nazionalita || "", sezione: scheda.sezione || "" });
    setEditAnag(true);
  };

  const saveAnag = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${scheda.id}`, anagForm);
      toast.success("Dati aggiornati!");
      setEditAnag(false);
      openScheda(scheda.id);
    } catch { toast.error("Errore aggiornamento"); }
    setSaving(false);
  };

  const toggleTessera = async (userId) => {
    try {
      const { data } = await axios.put(`${API}/admin/users/${userId}/tessera`, {});
      toast.success(data.tessera_pagata ? "Tessera segnata come pagata" : "Tessera segnata come non pagata");
      openScheda(userId);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento tessera"); }
  };

  const saveCVSection = async (field, values) => {
    if (!scheda) return;
    try {
      await axios.put(`${API}/cv/${scheda.id}`, { [field]: values });
      setScheda(p => ({ ...p, cv: { ...p.cv, [field]: values } }));
      toast.success("CV aggiornato");
    } catch { toast.error("Errore"); }
  };

  const addCVItem = async (field) => {
    const items = [...(scheda?.cv?.[field] || []), cvForm];
    await saveCVSection(field, items);
    setEditingCV(null); setCvForm({});
  };

  const removeCVItem = async (field, idx) => {
    const items = (scheda?.cv?.[field] || []).filter((_, i) => i !== idx);
    await saveCVSection(field, items);
  };

  const addComp = async (c) => { if (!c || scheda?.cv?.competenze?.includes(c)) return; await saveCVSection("competenze", [...(scheda?.cv?.competenze || []), c]); };

  const downloadCV = (userId) => {
    const u = users?.find(x => x.id === userId) || scheda;
    downloadAuthFile(`${API}/cv/genera-pdf/${userId}`, `CV_${u?.cognome || ''}_${u?.nome || ''}.pdf`);
  };

  const uploadPhotoAdmin = async (e, userId) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await axios.post(`${API}/upload/foto-profilo/${userId}`, fd); toast.success("Foto aggiornata"); openScheda(userId); } catch { toast.error("Errore"); }
  };

  const roleBadge = (r) => {
    const c = { admin: "bg-[#C9A84C]/10 text-[#C9A84C] border-[#C9A84C]/20", helper: "bg-[#00A0D1]/10 text-[#00A0D1] border-[#00A0D1]/20", iscritto: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", iscritto_base: "bg-white/5 text-white/60 border-white/10" };
    const labels = { admin: "admin", helper: "collaboratore", iscritto: "UGL", iscritto_base: "base" };
    return <Badge className={`${c[r] || c.iscritto_base} text-xs border`}>{labels[r] || r}</Badge>;
  };

  const searchedUsers = users.filter(u => `${u.nome} ${u.cognome} ${u.codice_fiscale} ${u.sezione}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searchedUsers);

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  // ─── Scheda Utente Detail View ───
  if (scheda) {
    const cv = scheda.cv || {};
    return (
      <div data-testid="user-scheda">
        <button onClick={() => setScheda(null)} className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-4" data-testid="back-to-users">
          <ArrowLeft className="w-4 h-4" /> Torna alla lista
        </button>
        {/* Header */}
        <div className="glass-card rounded-lg border border-[#C9A84C]/20 p-6 mb-4">
          <div className="flex items-start gap-5">
            <div className="relative group">
              {photoUrl ? (
                <img src={photoUrl} alt="Foto" className="w-20 h-20 rounded-xl object-cover border-2 border-[#C9A84C]/30" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-[#C9A84C]/10 border-2 border-[#C9A84C]/20 flex items-center justify-center overflow-hidden">
                  <User className="w-8 h-8 text-[#C9A84C]" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                <Camera className="w-5 h-5 text-white" />
                <input type="file" accept="image/*" onChange={e => uploadPhotoAdmin(e, scheda.id)} className="hidden" />
              </label>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{scheda.nome} {scheda.cognome}</h2>
                {roleBadge(scheda.ruolo)}
                {/* Toggle Attiva/Disattiva Account */}
                <button onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/users/${scheda.id}`, { attivo: !scheda.attivo });
                    toast.success(scheda.attivo ? "Account disattivato" : "Account attivato");
                    openScheda(scheda.id);
                  } catch (e) { console.error(e); toast.error("Errore"); }
                }} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${scheda.attivo ? 'bg-[#00A0D1]/15 text-[#5ba8d9] border-[#00A0D1]/25 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/25' : 'bg-white/5 text-white/40 border-white/15 hover:bg-emerald-500/15 hover:text-emerald-400 hover:border-emerald-500/25'}`} data-testid="toggle-attivo">
                  <div className={`w-7 h-4 rounded-full flex items-center transition-all ${scheda.attivo ? 'bg-[#5ba8d9] justify-end' : 'bg-white/20 justify-start'}`}>
                    <div className={`w-3 h-3 rounded-full mx-0.5 ${scheda.attivo ? 'bg-white' : 'bg-white/50'}`} />
                  </div>
                  {scheda.attivo ? 'Attivo' : 'Non Attivo'}
                </button>
                {/* Semaforo Tessera */}
                <button onClick={() => toggleTessera(scheda.id)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${scheda.tessera_pagata ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/25' : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'}`} data-testid="toggle-tessera">
                  <div className={`w-2.5 h-2.5 rounded-full ${scheda.tessera_pagata ? 'bg-emerald-400 shadow-sm shadow-emerald-400/50' : 'bg-red-400 shadow-sm shadow-red-400/50 animate-pulse'}`} />
                  {scheda.tessera_pagata ? 'Tessera Pagata' : 'Tessera Non Pagata'}
                </button>
                {/* Toggle Base/UGL */}
                {(scheda.ruolo === 'iscritto_base' || scheda.ruolo === 'iscritto') && (
                  <button onClick={async () => {
                    const newRole = scheda.ruolo === 'iscritto_base' ? 'iscritto' : 'iscritto_base';
                    try {
                      await axios.put(`${API}/admin/users/${scheda.id}`, { ruolo: newRole });
                      toast.success(newRole === 'iscritto' ? 'Promosso a Tesserato UGL' : 'Riportato a Tesserato Base');
                      openScheda(scheda.id);
                    } catch (e) { console.error(e); toast.error("Errore"); }
                  }} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${scheda.ruolo === 'iscritto' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25 hover:bg-white/5' : 'bg-white/5 text-white/40 border-white/15 hover:bg-emerald-500/15 hover:text-emerald-400 hover:border-emerald-500/25'}`} data-testid="toggle-ruolo">
                    <span>{scheda.ruolo === 'iscritto' ? '⬆ UGL' : '⬆ Promuovi a UGL'}</span>
                  </button>
                )}
              </div>
              {scheda.tessera_scadenza && scheda.tessera_pagata && <p className="text-[10px] text-emerald-400/50 mb-2">Scadenza: {scheda.tessera_scadenza}</p>}
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm text-white/50">
                <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-white/25" /><span className="font-mono text-xs">{scheda.codice_fiscale}</span></div>
                {scheda.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-white/25" />{scheda.email}</div>}
                {scheda.telefono && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-white/25" />{scheda.telefono}</div>}
                {scheda.sezione && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/25" />Sezione: {scheda.sezione}</div>}
                {scheda.data_nascita && <div className="flex items-center gap-2 text-xs text-white/40">Nascita: {scheda.data_nascita}</div>}
                {scheda.nazionalita && <div className="flex items-center gap-2 text-xs text-white/40">Nazionalità: {scheda.nazionalita}</div>}
                {scheda.indirizzo && <div className="flex items-center gap-2 text-xs text-white/40 col-span-2">Indirizzo: {scheda.indirizzo}</div>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" onClick={() => downloadCV(scheda.id)} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 text-xs" data-testid="download-user-cv"><Download className="w-3 h-3 mr-1" /> Scarica CV</Button>
                <Button size="sm" onClick={openEditAnag} variant="outline" className="border-white/15 text-white/60 bg-transparent hover:bg-white/5 text-xs" data-testid="edit-anag-btn"><Plus className="w-3 h-3 mr-1" /> Modifica Dati</Button>
                <Button size="sm" onClick={() => handleShowTotp(scheda.id)} variant="outline" className="border-[#00A0D1]/30 text-[#00A0D1] bg-transparent hover:bg-[#00A0D1]/10 text-xs" data-testid="show-totp-btn"><Eye className="w-3 h-3 mr-1" /> Mostra PIN</Button>
                <Button size="sm" onClick={() => handleResetTotp(scheda.id)} variant="outline" className="border-red-500/20 text-red-400 bg-transparent hover:bg-red-500/10 text-xs" data-testid="reset-totp-btn"><Key className="w-3 h-3 mr-1" /> Reset</Button>
                <Button size="sm" onClick={async () => {
                  const { data } = await axios.get(`${API}/admin/users/${scheda.id}/totp`);
                  const msg = `Gentile ${scheda.nome} ${scheda.cognome},\n\nsiamo lieti di informarLa che abbiamo attivato il nuovo servizio per i tesserati CAF di Lariano. Utilizzando il servizio base UGL Lariano potrà accedere al sito www.ugllariano.it nella sezione "Accedi" ed inserire i seguenti dati:\n\n📋 *Codice Fiscale:* ${scheda.codice_fiscale}\n🔑 *PIN:* ${data.pin}\n🔒 *Password:* ${data.password}\n\nPer qualsiasi necessità siamo a disposizione.\n\nCordiali saluti,\nUGL - Unione Zonale Lariano\nVia Roma 119, Lariano\n\n📞 Caf Patronato - Sportello Pubblico: 351 762 4080\n📞 Assistenza e appuntamenti: 352 035 5129\n📞 UGL Lariano: 351 413 4225`;
                  const tel = scheda.telefono ? scheda.telefono.replace(/\s/g, '') : '';
                  const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
                  const url = tel
                    ? (isMobile ? `https://wa.me/39${tel}?text=${encodeURIComponent(msg)}` : `https://web.whatsapp.com/send?phone=39${tel}&text=${encodeURIComponent(msg)}`)
                    : (isMobile ? `https://wa.me/?text=${encodeURIComponent(msg)}` : `https://web.whatsapp.com/send?text=${encodeURIComponent(msg)}`);
                  (window.top || window).open(url, '_blank');
                }} className="bg-[#25D366] text-white hover:bg-[#25D366]/90 text-xs" data-testid="wa-send-credentials">
                  <MessageSquare className="w-3 h-3 mr-1" /> Invia Credenziali WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* CV Sections */}
        {[
          { key: "formazione", icon: GraduationCap, title: "Formazione", fields: ["titolo", "istituto", "anno_inizio", "anno_fine"], display: (f) => <><p className="text-sm font-medium text-white/80">{f.titolo}</p><p className="text-xs text-white/30">{f.istituto} | {f.anno_inizio} - {f.anno_fine || "In corso"}</p></> },
          { key: "esperienze", icon: Briefcase, title: "Esperienze", fields: ["ruolo", "azienda", "anno_inizio", "anno_fine", "descrizione"], display: (e) => <><p className="text-sm font-medium text-white/80">{e.ruolo}</p><p className="text-xs text-white/30">{e.azienda} | {e.anno_inizio} - {e.anno_fine || "In corso"}</p>{e.descrizione && <p className="text-xs text-white/20 mt-1">{e.descrizione}</p>}</> },
        ].map(section => (
          <div key={section.key} className="glass-card rounded-lg border border-white/8 p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><section.icon className="w-4 h-4 text-[#00A0D1]" />{section.title}</h3>
              <Button size="sm" variant="outline" onClick={() => { setEditingCV(section.key); setCvForm({}); }} className="text-xs border-white/10 text-white/50 bg-transparent"><Plus className="w-3 h-3 mr-1" /> Aggiungi</Button>
            </div>
            {!cv[section.key]?.length && <p className="text-sm text-white/25">Nessun dato</p>}
            <div className="space-y-2">{cv[section.key]?.map((item, i) => (
              <div key={i} className="flex items-start justify-between p-3 bg-white/[0.02] rounded-md border border-white/5">
                <div>{section.display(item)}</div>
                <Button size="sm" variant="ghost" onClick={() => removeCVItem(section.key, i)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}</div>
          </div>
        ))}

        {/* Competenze */}
        <div className="glass-card rounded-lg border border-white/8 p-5 mb-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit' }}><Wrench className="w-4 h-4 text-[#00A0D1]" />Competenze</h3>
          <div className="flex flex-wrap gap-2 mb-3">{cv.competenze?.map((c, i) => (
            <Badge key={i} className="bg-[#00A0D1]/10 text-[#00A0D1] border border-[#00A0D1]/20 text-xs cursor-pointer hover:bg-red-500/10 hover:text-red-400" onClick={() => removeCVItem("competenze", i)}>{c} &times;</Badge>
          ))}</div>
          <Select onValueChange={addComp}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm"><SelectValue placeholder="Aggiungi competenza..." /></SelectTrigger>
            <SelectContent className="bg-[#0d1b2e] border-white/10 text-white max-h-60">{presetComp.filter(c => !cv.competenze?.includes(c)).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Lingue */}
        <div className="glass-card rounded-lg border border-white/8 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Languages className="w-4 h-4 text-[#00A0D1]" />Lingue</h3>
            <Button size="sm" variant="outline" onClick={() => { setEditingCV("lingua"); setCvForm({ nome: "", parlato: 5, scritto: 5, letto: 5 }); }} className="text-xs border-white/10 text-white/50 bg-transparent"><Plus className="w-3 h-3 mr-1" /> Aggiungi</Button>
          </div>
          {cv.lingue?.map((l, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-md border border-white/5 mb-2">
              <div className="flex-1">
                <p className="text-sm font-medium text-white/80 mb-1">{typeof l === 'string' ? l : l.nome}</p>
                {typeof l === 'object' && <div className="grid grid-cols-3 gap-3">
                  {["parlato","scritto","letto"].map(k => <div key={k}><p className="text-[10px] text-white/30">{k}</p><div className="flex items-center gap-1"><div className="flex-1 h-1.5 bg-white/10 rounded-full"><div className="h-full bg-[#C9A84C] rounded-full" style={{width:`${(l[k]||0)*10}%`}}/></div><span className="text-[10px] text-[#C9A84C] w-4 text-right">{l[k]}</span></div></div>)}
                </div>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeCVItem("lingue", i)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>

        {/* Documenti Utente */}
        <div className="glass-card rounded-lg border border-white/8 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><FileText className="w-4 h-4 text-[#00A0D1]" />Documenti</h3>
            <div>
              <input id={`doc-upload-${scheda.id}`} type="file" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                const fd = new FormData(); fd.append("file", file);
                try {
                  await axios.post(`${API}/upload/documento?tipo=personale&target_user_id=${scheda.id}`, fd);
                  toast.success("Documento caricato!");
                  openScheda(scheda.id);
                } catch { toast.error("Errore upload"); }
                e.target.value = '';
              }} className="hidden" />
              <Button size="sm" variant="outline" className="text-xs border-white/10 text-white/50 bg-transparent hover:bg-white/5" data-testid="upload-user-doc"
                onClick={() => document.getElementById(`doc-upload-${scheda.id}`).click()}>
                <Upload className="w-3 h-3 mr-1" /> Carica
              </Button>
            </div>
          </div>
          {!scheda.documenti?.length && <p className="text-sm text-white/25">Nessun documento</p>}
          {scheda.documenti?.map(d => (
            <div key={d.id} className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-md border border-white/5 mb-2">
              <FileText className="w-4 h-4 text-[#C9A84C] flex-shrink-0" />
              <p className="text-sm text-white/60 flex-1 truncate">{d.original_filename}</p>
              <p className="text-[10px] text-white/20">{new Date(d.created_at).toLocaleDateString('it-IT')}</p>
              <Button size="sm" variant="ghost" onClick={() => downloadAuthFile(`${API}/files/${d.storage_path}`, d.original_filename)}
                className="text-white/30 hover:text-[#00A0D1]"><Download className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={async () => {
                if (!window.confirm("Eliminare questo documento?")) return;
                try { await axios.delete(`${API}/documenti/${d.id}`); toast.success("Eliminato"); openScheda(scheda.id); } catch { toast.error("Errore"); }
              }} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>

        {/* CV Edit Dialogs */}
        <Dialog open={editingCV === "formazione"} onOpenChange={() => setEditingCV(null)}>
          <DialogContent className="sm:max-w-md bg-[#0d1b2e] border-white/10 text-white">
            <DialogHeader><DialogTitle className="text-white">Aggiungi Formazione</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-white/60">Titolo *</Label><Input value={cvForm.titolo||""} onChange={e=>setCvForm(p=>({...p,titolo:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-xs text-white/60">Istituto</Label><Input value={cvForm.istituto||""} onChange={e=>setCvForm(p=>({...p,istituto:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-white/60">Inizio</Label><Input value={cvForm.anno_inizio||""} onChange={e=>setCvForm(p=>({...p,anno_inizio:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
                <div><Label className="text-xs text-white/60">Fine</Label><Input value={cvForm.anno_fine||""} onChange={e=>setCvForm(p=>({...p,anno_fine:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setEditingCV(null)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button><Button onClick={()=>addCVItem("formazione")} className="bg-[#C9A84C] text-[#001830]">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={editingCV === "esperienze"} onOpenChange={() => setEditingCV(null)}>
          <DialogContent className="sm:max-w-md bg-[#0d1b2e] border-white/10 text-white">
            <DialogHeader><DialogTitle className="text-white">Aggiungi Esperienza</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-white/60">Ruolo *</Label><Input value={cvForm.ruolo||""} onChange={e=>setCvForm(p=>({...p,ruolo:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-xs text-white/60">Azienda</Label><Input value={cvForm.azienda||""} onChange={e=>setCvForm(p=>({...p,azienda:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-white/60">Inizio</Label><Input value={cvForm.anno_inizio||""} onChange={e=>setCvForm(p=>({...p,anno_inizio:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
                <div><Label className="text-xs text-white/60">Fine</Label><Input value={cvForm.anno_fine||""} onChange={e=>setCvForm(p=>({...p,anno_fine:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              </div>
              <div><Label className="text-xs text-white/60">Descrizione</Label><Textarea value={cvForm.descrizione||""} onChange={e=>setCvForm(p=>({...p,descrizione:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" rows={2} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setEditingCV(null)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button><Button onClick={()=>addCVItem("esperienze")} className="bg-[#C9A84C] text-[#001830]">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={editingCV === "lingua"} onOpenChange={() => setEditingCV(null)}>
          <DialogContent className="sm:max-w-md bg-[#0d1b2e] border-white/10 text-white">
            <DialogHeader><DialogTitle className="text-white">Aggiungi Lingua</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Select value={cvForm.nome||""} onValueChange={v=>setCvForm(p=>({...p,nome:v}))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Lingua..." /></SelectTrigger>
                <SelectContent className="bg-[#0d1b2e] border-white/10 text-white">{presetLingue.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {["parlato","scritto","letto"].map(k=>(
                <div key={k}><div className="flex justify-between mb-1"><Label className="text-xs text-white/60 capitalize">{k}</Label><span className="text-sm font-bold text-[#C9A84C]">{cvForm[k]||5}/10</span></div>
                <Slider value={[cvForm[k]||5]} onValueChange={v=>setCvForm(p=>({...p,[k]:v[0]}))} max={10} min={1} step={1}/></div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setEditingCV(null)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button><Button onClick={()=>addCVItem("lingue")} disabled={!cvForm.nome} className="bg-[#C9A84C] text-[#001830]">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PIN Dialog - inside scheda view */}
        <Dialog open={!!showPin} onOpenChange={() => setShowPin(null)}>
          <DialogContent className="sm:max-w-sm bg-[#0d1b2e] border-white/10 text-white" data-testid="pin-dialog">
            <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Credenziali di Accesso</DialogTitle><DialogDescription className="text-white/40">Comunica PIN e Password all'utente</DialogDescription></DialogHeader>
            {showPin && <div className="space-y-4 py-2">
              <div>
                <p className="text-[10px] text-white/30 uppercase mb-2 text-center">PIN (8 cifre)</p>
                <div className="flex justify-center gap-1.5">
                  {(showPin.pin || "").split("").map((d, i) => (
                    <div key={i} className="w-9 h-12 rounded-lg bg-white/5 border border-[#5ba8d9]/30 flex items-center justify-center">
                      <span className="text-xl font-bold text-[#5ba8d9]">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] text-white/30 uppercase mb-2 text-center">Password (4 lettere)</p>
                <div className="flex justify-center gap-2">
                  {(showPin.password || "").split("").map((d, i) => (
                    <div key={i} className="w-11 h-12 rounded-lg bg-white/5 border border-[#C9A84C]/30 flex items-center justify-center">
                      <span className="text-xl font-bold text-[#C9A84C]">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>}
            <DialogFooter><Button onClick={() => setShowPin(null)} className="bg-[#C9A84C] text-[#001830]">Chiudi</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modifica Dati Anagrafici Dialog */}
        <Dialog open={editAnag} onOpenChange={setEditAnag}>
          <DialogContent className="sm:max-w-lg bg-[#13243d] border-[#1e3456] text-white" data-testid="edit-anag-dialog">
            <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Modifica Dati Anagrafici</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto">
              <div><Label className="text-xs text-white/60">Nome</Label><Input value={anagForm.nome||""} onChange={e=>setAnagForm(p=>({...p,nome:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div><Label className="text-xs text-white/60">Cognome</Label><Input value={anagForm.cognome||""} onChange={e=>setAnagForm(p=>({...p,cognome:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div><Label className="text-xs text-white/60">Data di Nascita</Label><Input type="date" value={anagForm.data_nascita||""} onChange={e=>setAnagForm(p=>({...p,data_nascita:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div><Label className="text-xs text-white/60">Nazionalità</Label><Input value={anagForm.nazionalita||""} onChange={e=>setAnagForm(p=>({...p,nazionalita:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white" placeholder="Italiana"/></div>
              <div><Label className="text-xs text-white/60">Email</Label><Input value={anagForm.email||""} onChange={e=>setAnagForm(p=>({...p,email:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div><Label className="text-xs text-white/60">Telefono</Label><Input value={anagForm.telefono||""} onChange={e=>setAnagForm(p=>({...p,telefono:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div className="col-span-2"><Label className="text-xs text-white/60">Indirizzo</Label><Input value={anagForm.indirizzo||""} onChange={e=>setAnagForm(p=>({...p,indirizzo:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white" placeholder="Via, Citta, CAP"/></div>
              <div><Label className="text-xs text-white/60">Sezione</Label><Input value={anagForm.sezione||""} onChange={e=>setAnagForm(p=>({...p,sezione:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setEditAnag(false)} className="border-[#1e3456] text-white/50 bg-transparent">Annulla</Button>
              <Button onClick={saveAnag} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Salva"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }


  const handleImportPDF = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/import-tesserati`, fd);
      setImportResult(data);
      toast.success(`Importati ${data.importati} tesserati!`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore importazione"); }
    e.target.value = '';
  };

  // ─── Users List ───
  return (
    <div data-testid="user-management">
      <div className="flex items-center justify-between mb-4">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca utenti..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="search-users" /></div>
        <div className="flex items-center gap-2">
          <div>
            <input id="import-pdf" type="file" accept=".pdf" onChange={handleImportPDF} className="hidden" />
            <Button size="sm" variant="outline" onClick={() => document.getElementById('import-pdf').click()} className="border-[#1e3456] text-white/50 bg-transparent hover:bg-[#1a3a5c] text-xs" data-testid="import-pdf-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Importa PDF
            </Button>
          </div>
          <Button data-testid="create-user-btn" onClick={() => setShowCreate(true)} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-medium">
            <UserPlus className="w-4 h-4 mr-2" /> Nuovo Utente
          </Button>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <div className="bg-[#13243d] border border-emerald-500/20 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-emerald-400">Importazione completata: {importResult.importati} tesserati</p>
            <button onClick={() => setImportResult(null)} className="text-white/30 hover:text-white text-xs">✕</button>
          </div>
          {importResult.saltati > 0 && <p className="text-xs text-amber-400 mb-2">{importResult.saltati} saltati (già presenti o dati incompleti)</p>}
          {importResult.dettaglio_importati?.length > 0 && (
            <div className="max-h-32 overflow-y-auto space-y-1">
              {importResult.dettaglio_importati.map((u, i) => (
                <div key={i} className="flex items-center gap-3 text-xs text-white/50 bg-white/[0.02] rounded px-2 py-1">
                  <span className="text-white/70 font-medium">{u.nome}</span>
                  <span className="font-mono text-[10px]">{u.cf}</span>
                  <span className="text-emerald-400/70">PIN: {u.pin}</span>
                  <span className="text-[#C9A84C]/70">PWD: {u.password}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-white/20 mt-2">Gli utenti importati sono in stato "non attivo". Attivali manualmente dalla scheda.</p>
        </div>
      )}

      <div className="space-y-2">
        {pg.paged.map(u => (
          <div key={u.id} className="glass-card rounded-lg border border-white/8 p-4 flex items-center gap-4 cursor-pointer hover:border-[#00A0D1]/20 transition-all" onClick={() => openScheda(u.id)} data-testid={`user-row-${u.id}`}>
            <div className="w-9 h-9 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-[#C9A84C]">{u.nome?.[0]}{u.cognome?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{u.nome} {u.cognome}</p>
              <p className="text-[10px] font-mono text-white/30">{u.codice_fiscale}</p>
            </div>
            <div className="hidden sm:block">{roleBadge(u.ruolo)}</div>
            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${u.tessera_pagata ? 'bg-emerald-400 shadow-sm shadow-emerald-400/50' : 'bg-red-400 shadow-sm shadow-red-400/50'}`} title={u.tessera_pagata ? 'Tessera pagata' : 'Tessera non pagata'} />
            <div className="hidden md:block text-xs text-white/30">{u.sezione || "-"}</div>
            {userRole === "admin" && (
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(u.id); }} className="text-white/20 hover:text-red-400 hover:bg-red-500/5" data-testid={`delete-user-${u.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
      </div>
      <PaginationBar {...pg} />

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg bg-[#0d1b2e] border-white/10 text-white" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Nuovo Utente</DialogTitle><DialogDescription className="text-white/40">Crea un nuovo iscritto</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="col-span-2"><Label className="text-xs text-white/60">Codice Fiscale *</Label><Input data-testid="new-user-cf" value={form.codice_fiscale} onChange={e=>setForm(p=>({...p,codice_fiscale:e.target.value.toUpperCase()}))} className="mt-1 uppercase bg-white/5 border-white/10 text-white" maxLength={16}/></div>
            <div><Label className="text-xs text-white/60">Nome *</Label><Input data-testid="new-user-nome" value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white"/></div>
            <div><Label className="text-xs text-white/60">Cognome *</Label><Input data-testid="new-user-cognome" value={form.cognome} onChange={e=>setForm(p=>({...p,cognome:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white"/></div>
            <div><Label className="text-xs text-white/60">Data di Nascita</Label><Input type="date" value={form.data_nascita} onChange={e=>setForm(p=>({...p,data_nascita:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white"/></div>
            <div><Label className="text-xs text-white/60">Nazionalità</Label><Input value={form.nazionalita} onChange={e=>setForm(p=>({...p,nazionalita:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" placeholder="Italiana"/></div>
            <div><Label className="text-xs text-white/60">Email</Label><Input data-testid="new-user-email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white"/></div>
            <div><Label className="text-xs text-white/60">Telefono</Label><Input data-testid="new-user-tel" value={form.telefono} onChange={e=>setForm(p=>({...p,telefono:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white"/></div>
            <div className="col-span-2"><Label className="text-xs text-white/60">Indirizzo</Label><Input value={form.indirizzo} onChange={e=>setForm(p=>({...p,indirizzo:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" placeholder="Via, Citta, CAP"/></div>
            <div><Label className="text-xs text-white/60">Ruolo</Label><Select value={form.ruolo} onValueChange={v=>setForm(p=>({...p,ruolo:v}))}><SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white" data-testid="new-user-ruolo"><SelectValue/></SelectTrigger><SelectContent className="bg-[#0d1b2e] border-white/10 text-white"><SelectItem value="iscritto">Tesserato UGL</SelectItem><SelectItem value="iscritto_base">Tesserato Base</SelectItem><SelectItem value="helper">Collaboratore</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs text-white/60">Sezione</Label><Input data-testid="new-user-sezione" value={form.sezione} onChange={e=>setForm(p=>({...p,sezione:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowCreate(false)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button>
            <Button data-testid="submit-create-user" onClick={handleCreate} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Dialog */}
      <Dialog open={!!showPin} onOpenChange={()=>setShowPin(null)}>
        <DialogContent className="sm:max-w-sm bg-[#0d1b2e] border-white/10 text-white" data-testid="pin-dialog-list">
          <DialogHeader><DialogTitle className="text-white">Credenziali di Accesso</DialogTitle><DialogDescription className="text-white/40">Comunica PIN e Password all'utente</DialogDescription></DialogHeader>
          {showPin && <div className="space-y-4 py-2">
            <div><p className="text-[10px] text-white/30 uppercase mb-2 text-center">PIN (8 cifre)</p>
              <div className="flex justify-center gap-1.5">{(showPin.pin || "").split("").map((d, i) => (<div key={i} className="w-9 h-12 rounded-lg bg-white/5 border border-[#5ba8d9]/30 flex items-center justify-center"><span className="text-xl font-bold text-[#5ba8d9]">{d}</span></div>))}</div>
            </div>
            <div><p className="text-[10px] text-white/30 uppercase mb-2 text-center">Password (4 lettere)</p>
              <div className="flex justify-center gap-2">{(showPin.password || "").split("").map((d, i) => (<div key={i} className="w-11 h-12 rounded-lg bg-white/5 border border-[#C9A84C]/30 flex items-center justify-center"><span className="text-xl font-bold text-[#C9A84C]">{d}</span></div>))}</div>
            </div>
          </div>}
          <DialogFooter><Button onClick={()=>setShowPin(null)} className="bg-[#C9A84C] text-[#001830]">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
